/*
 * Nestro motion system
 * Scroll-triggered reveals and parallax, no dependencies. Scrolling itself is
 * left to the browser on purpose — hijacking the wheel to add inertia fought
 * the native scroll and made the store hard to navigate.
 *
 * The `nestro-motion` class on <html> is set by an inline head script and is what
 * arms the hidden-until-revealed CSS; this file must set `nestro-motion-ready`
 * or that head script strips the class again and everything renders plainly.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  if (prefersReduced || designMode) {
    root.classList.remove('nestro-motion');
    return;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  /* ----------------------------------------------------------------
   * 1. Word splitting for headline reveals
   * ---------------------------------------------------------------- */
  function splitWords(el) {
    if (el.dataset.mSplit) return false;

    var text = el.textContent.trim();
    if (!text || text.length > 140) return false;

    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) nodes.push(node);
    }
    if (!nodes.length) return false;

    var index = 0;
    nodes.forEach(function (textNode) {
      var fragment = document.createDocumentFragment();
      var parts = textNode.nodeValue.split(/(\s+)/);

      parts.forEach(function (part) {
        if (!part) return;

        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }

        var outer = document.createElement('span');
        outer.className = 'm-word';
        var inner = document.createElement('span');
        inner.className = 'm-word__i';
        inner.style.setProperty('--w', String(index++));
        inner.textContent = part;
        outer.appendChild(inner);
        fragment.appendChild(outer);
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    el.dataset.mSplit = 'true';
    return true;
  }

  /* ----------------------------------------------------------------
   * 2. Reveal on scroll
   * ---------------------------------------------------------------- */
  var FADE_SELECTOR = [
    '.shopify-section .text-block',
    '.shopify-section .icon-block',
    '.shopify-section .resource-list__item',
    '.shopify-section .email-signup-block',
    '.shopify-section .accordion',
    '.shopify-section .button',
    '.shopify-section .media-with-content__media',
  ].join(',');

  function initReveals() {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    document.querySelectorAll('.shopify-section').forEach(function (section) {
      // Headlines get a per-word rise; everything else gets a soft lift.
      section.querySelectorAll('.text-block h1, .text-block h2, .text-block h3').forEach(function (heading) {
        var block = heading.closest('.text-block');
        if (block && block.closest('.product-card')) return;
        if (splitWords(heading)) {
          heading.classList.add('m-split');
          if (block) block.dataset.mSkip = 'true';
        }
      });

      var items = Array.prototype.slice.call(section.querySelectorAll(FADE_SELECTOR));
      var stagger = 0;

      items.forEach(function (item) {
        if (item.dataset.mSkip) return;
        if (item.closest('.product-card') && !item.classList.contains('resource-list__item')) return;
        item.classList.add('m-fade');
        item.style.setProperty('--i', String(stagger++ % 8));
        observer.observe(item);
      });

      section.querySelectorAll('.m-split').forEach(function (heading) {
        observer.observe(heading);
      });
    });
  }

  /* ----------------------------------------------------------------
   * 3. Media parallax
   * ---------------------------------------------------------------- */
  function initParallax() {
    var targets = Array.prototype.slice.call(
      document.querySelectorAll('.media-with-content__media img, [data-parallax] img')
    );
    if (!targets.length) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    targets.forEach(function (img) {
      img.classList.add('m-parallax');
    });

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight;

      targets.forEach(function (img) {
        var rect = img.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewportHeight) return;

        // -1 when entering from below, 1 when leaving through the top.
        var progress = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
        var shift = clamp(progress, -1, 1) * -18;
        img.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0) scale(1.06)';
      });

      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  function init() {
    initReveals();
    initParallax();
    root.classList.add('nestro-motion-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Keep the theme editor usable: re-scan when a section is re-rendered.
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(initReveals, 60);
  });
})();
